import { z } from 'zod';
import { AccommodationType, accommodationOptions } from '../../constants/accommodation';

export const inquirySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .min(6, 'Phone number is too short'),
  accommodations: z
    .record(
      z.enum(['mudhouse', 'mudhouse-dormitory', 'premium-twin-bed-cottage']),
      z.number().min(0).max(10),
    )
    .default({}),
  additionalBeds: z.number().min(0).default(0),
  checkIn: z.string().optional(),
  checkOut: z.string().optional(),
  guests: z.string().optional(),
  message: z.string().optional(),
});

export type InquiryFormValues = z.infer<typeof inquirySchema>;

export function calculateCapacity(
  accommodations: Partial<Record<AccommodationType, number>>,
  additionalBeds: number = 0,
) {
  const baseCapacity = Object.entries(accommodations).reduce((acc, [type, count]) => {
    const option = accommodationOptions.find((o) => o.value === type);
    return acc + (option?.capacity ?? 0) * (count ?? 0);
  }, 0);
  // Only mudhouse and premium-twin-bed-cottage can have additional beds (not dormitory)
  const maxAdditionalBeds =
    (accommodations['mudhouse'] ?? 0) + (accommodations['premium-twin-bed-cottage'] ?? 0);
  return baseCapacity + Math.min(additionalBeds, maxAdditionalBeds);
}

export function calculateTotalRooms(
  accommodations: Partial<Record<AccommodationType, number>>,
) {
  return Object.values(accommodations).reduce((a, b) => a + (b ?? 0), 0);
}
